import { Cache } from 'swr';

const CACHE_KEY = 'swr-session-cache';

/**
 * 세션 스토리지 기반 SWR 캐시 provider
 * 브라우저 세션이 유지되는 동안만 캐시가 유지됩니다.
 */
export function sessionCacheProvider(): Cache<any> {
  const map = new Map();

  if (typeof window !== 'undefined') {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        for (const [key, value] of parsed) {
          if (value && typeof value === 'object' && 'data' in value) {
            const cacheKey = Array.isArray(key) ? JSON.stringify(key) : String(key);
            map.set(cacheKey, {
              data: value.data,
              error: value.error || undefined,
              isValidating: false,
              isLoading: false,
            });
          } else {
            const cacheKey = Array.isArray(key) ? JSON.stringify(key) : String(key);
            map.set(cacheKey, value);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to restore SWR cache from sessionStorage:', error);
    }
  }

  const saveCache = () => {
    if (typeof window !== 'undefined') {
      try {
        const entriesToSave: Array<[any, any]> = [];
        for (const [key, value] of map.entries()) {
          if (value && typeof value === 'object' && 'data' in value) {
            entriesToSave.push([key, { data: value.data }]);
          } else {
            entriesToSave.push([key, value]);
          }
        }
        const serialized = JSON.stringify(entriesToSave);
        sessionStorage.setItem(CACHE_KEY, serialized);
      } catch (error) {
        console.warn('Failed to save SWR cache to sessionStorage:', error);
      }
    }
  };

  let saveTimeout: NodeJS.Timeout | null = null;
  const debouncedSave = () => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    saveTimeout = setTimeout(saveCache, 100);
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', saveCache);
  }

  return {
    get: (key: string | any[]) => {
      const cacheKey = Array.isArray(key) ? JSON.stringify(key) : String(key);
      return map.get(cacheKey);
    },
    set: (key: string | any[], value: any) => {
      const cacheKey = Array.isArray(key) ? JSON.stringify(key) : String(key);
      map.set(cacheKey, value);
      saveCache(); // 즉시 저장 - 새로고침 시 캐시 유실 방지
      debouncedSave(); // 연속 업데이트 시 추가 저장
      return map;
    },
    delete: (key: string | any[]) => {
      const cacheKey = Array.isArray(key) ? JSON.stringify(key) : String(key);
      const deleted = map.delete(cacheKey);
      debouncedSave();
      return deleted;
    },
    keys: () => {
      const keys = Array.from(map.keys()).map((key) => {
        try {
          if (typeof key === 'string' && key.startsWith('[')) {
            const parsed = JSON.parse(key);
            return Array.isArray(parsed) ? JSON.stringify(parsed) : String(parsed);
          }
          return String(key);
        } catch {
          return String(key);
        }
      });
      return keys[Symbol.iterator]();
    },
  };
}
